import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogContentText, DialogTitle, Divider, FormControl, FormControlLabel, InputAdornment,
  Radio, RadioGroup, Stack, TextField, Typography,
} from '@mui/material';
import { apiService, PaymentOption, HistorialFinanciero } from '../../services/api';

// Corregir el cobro de una instalación ya cerrada.
//
// El técnico cierra en la calle, con el cliente delante, y se equivoca de dos maneras
// distintas: digita mal el monto, o marca "transferencia" creyendo que eso significa "no es
// mío" cuando el cliente le transfirió a SU cuenta. Las dos descuadran la contabilidad, así
// que las dos se arreglan desde el mismo sitio y en un solo movimiento.
//
// El diálogo enseña el efecto sobre el efectivo ANTES de guardar: quien corrige tiene que
// poder ver que el saldo del técnico se mueve, porque ese es justo el punto de la corrección.

export interface CobroActual {
  installationId: string;
  cliente?: string | null;
  monto: number;
  method?: string;
  toTechnician?: boolean;
  etiqueta?: string;
}

interface Props {
  cobro: CobroActual | null;
  onCerrar: () => void;
  onCorregido: (mensaje: string) => void;
}

const OPCIONES: Array<{ valor: PaymentOption; titulo: string; detalle: string }> = [
  { valor: 'cash', titulo: 'Efectivo al técnico', detalle: 'El cliente le pagó en mano. El técnico se lo debe a la empresa.' },
  { valor: 'transfer_technician', titulo: 'Transferencia al técnico', detalle: 'Cayó en la cuenta personal del técnico. Se lo debe igual que el efectivo.' },
  { valor: 'transfer_company', titulo: 'Transferencia a Driports', detalle: 'Entró directo a la cuenta de la empresa. El técnico no tocó ese dinero.' },
  { valor: 'none', titulo: 'Sin cobrar todavía', detalle: 'Queda registrado como ingreso pendiente, sin cargarle nada al técnico.' },
];

// De lo guardado a la opción que se ve marcada. Los registros viejos no tienen destinatario:
// ahí manda el efectivo, que es como se decidía antes de separar las dos preguntas.
function opcionDe(cobro: { method?: string; toTechnician?: boolean }): PaymentOption {
  if (!cobro.method || cobro.method === 'none') return 'none';
  if (cobro.method === 'cash') return 'cash';
  return cobro.toTechnician === true ? 'transfer_technician' : 'transfer_company';
}

function loRecibeElTecnico(op: PaymentOption) {
  return op === 'cash' || op === 'transfer_technician';
}

function fmt(n: number) {
  return `RD$${(n ?? 0).toLocaleString('es-DO', { minimumFractionDigits: 0 })}`;
}

const CorregirCobroDialog: React.FC<Props> = ({ cobro, onCerrar, onCorregido }) => {
  const [monto, setMonto] = useState('');
  const [opcion, setOpcion] = useState<PaymentOption>('cash');
  const [motivo, setMotivo] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [historial, setHistorial] = useState<HistorialFinanciero | null>(null);
  // El punto de partida contra el que se mide si hubo cambio. Empieza en lo que trae quien
  // abre el diálogo y se corrige cuando llega el historial, que es la fuente autorizada:
  // una cita con varios equipos se lista por equipo, pero el cobro fue UNO y se corrige
  // entero. Sin esto, corregir desde la lista mandaría la parte creyendo que es el total.
  const [base, setBase] = useState<{ monto: number; opcion: PaymentOption }>({ monto: 0, opcion: 'none' });

  useEffect(() => {
    if (!cobro) return;
    const inicial = { monto: cobro.monto ?? 0, opcion: opcionDe(cobro) };
    setBase(inicial);
    setMonto(String(inicial.monto));
    setOpcion(inicial.opcion);
    setMotivo('');
    setError(null);
    setHistorial(null);

    let vigente = true;
    apiService.getInstallationFinancialHistory(cobro.installationId)
      .then(r => {
        if (!vigente || !r.success || !r.data) return;
        setHistorial(r.data);
        const real = { monto: r.data.cobroActual.amount, opcion: opcionDe(r.data.cobroActual) };
        setBase(real);
        // Solo se reescriben los campos si el usuario todavía no los tocó: el valor real
        // llega medio segundo después de abrir y no debe pisar lo que ya esté escribiendo.
        setMonto(m => (m === String(inicial.monto) ? String(real.monto) : m));
        setOpcion(o => (o === inicial.opcion ? real.opcion : o));
      })
      .catch(() => { /* el historial es contexto, no bloquea la corrección */ });
    return () => { vigente = false; };
  }, [cobro]);

  if (!cobro) return null;

  const opcionOriginal = base.opcion;
  const montoNum = Number(monto);
  const montoValido = Number.isFinite(montoNum) && montoNum >= 0;
  const huboCambio = montoValido && (montoNum !== base.monto || opcion !== opcionOriginal);

  // Lo que el técnico le debe a la empresa por este cobro, antes y después.
  const cargaAntes = loRecibeElTecnico(opcionOriginal) ? base.monto : 0;
  const cargaDespues = loRecibeElTecnico(opcion) ? (montoValido ? montoNum : 0) : 0;
  const delta = cargaDespues - cargaAntes;

  const guardar = async () => {
    if (!montoValido) { setError('Escribe un monto válido'); return; }
    if (!huboCambio) { setError('No cambiaste nada: el monto y la forma de cobro son los mismos'); return; }
    if (motivo.trim().length < 5) { setError('Explica el motivo (mínimo 5 caracteres)'); return; }

    setGuardando(true);
    setError(null);
    try {
      const res = await apiService.correctInstallationPayment(cobro.installationId, {
        ...(montoNum !== base.monto ? { newAmount: montoNum } : {}),
        ...(opcion !== opcionOriginal ? { newPaymentOption: opcion } : {}),
        reason: motivo.trim(),
      });
      if (res.success && res.data) {
        const d = res.data;
        const partes = [`${fmt(d.anterior.amount)} ${d.anterior.etiqueta} → ${fmt(d.nuevo.amount)} ${d.nuevo.etiqueta}.`];
        if (d.delta !== 0) {
          partes.push(`El efectivo que debe el técnico ${d.delta > 0 ? 'sube' : 'baja'} ${fmt(Math.abs(d.delta))}` +
            (d.nuevoEfectivoDelTecnico !== null ? `, queda en ${fmt(d.nuevoEfectivoDelTecnico)}.` : '.'));
        }
        if (d.instalacionesAfectadas > 1) {
          partes.push(`Se repartió entre los ${d.instalacionesAfectadas} equipos de la cita.`);
        }
        if (d.invoiceWarning) partes.push(d.invoiceWarning);
        onCorregido(partes.join(' '));
        onCerrar();
      } else {
        setError(res.message || 'No se pudo corregir el cobro');
      }
    } catch (e: any) {
      setError(e.response?.data?.message || 'Error al corregir el cobro');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Dialog open onClose={guardando ? undefined : onCerrar} maxWidth="sm" fullWidth>
      <DialogTitle>Corregir el cobro</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          {cobro.cliente ? <>Instalación de <strong>{cobro.cliente}</strong>. </> : null}
          Quedó registrado como <strong>{fmt(base.monto)}</strong>
          {historial?.cobroActual.etiqueta || cobro.etiqueta
            ? <> — {(historial?.cobroActual.etiqueta || cobro.etiqueta || '').toLowerCase()}</>
            : null}.
          {historial && historial.instalacionesDelGrupo > 1 && (
            <> Esta cita tiene <strong>{historial.instalacionesDelGrupo} equipos</strong>: el monto de arriba es el
            total de la cita y se reparte entre ellos.</>
          )}
        </DialogContentText>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Stack spacing={2.5}>
          <TextField
            label={historial && historial.instalacionesDelGrupo > 1 ? 'Monto total de la cita' : 'Monto cobrado'}
            type="number"
            value={monto}
            onChange={e => setMonto(e.target.value)}
            fullWidth
            autoFocus
            InputProps={{ startAdornment: <InputAdornment position="start">RD$</InputAdornment> }}
          />

          <FormControl>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>¿Quién recibió el dinero?</Typography>
            <RadioGroup value={opcion} onChange={e => setOpcion(e.target.value as PaymentOption)}>
              {OPCIONES.map(o => (
                <FormControlLabel
                  key={o.valor}
                  value={o.valor}
                  control={<Radio size="small" />}
                  sx={{ alignItems: 'flex-start', mb: 0.5, '& .MuiRadio-root': { pt: 0.25 } }}
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight={600}>{o.titulo}</Typography>
                      <Typography variant="caption" color="text.secondary">{o.detalle}</Typography>
                    </Box>
                  }
                />
              ))}
            </RadioGroup>
          </FormControl>

          <TextField
            label="Motivo de la corrección"
            value={motivo}
            onChange={e => setMotivo(e.target.value)}
            fullWidth
            multiline
            rows={2}
            placeholder="Ej: el cliente transfirió a la cuenta de la empresa, no a la del técnico"
            helperText="Queda en el historial del técnico y en la auditoría"
          />

          {huboCambio && (
            <Alert severity={delta === 0 ? 'info' : 'warning'}>
              {delta === 0
                ? 'El efectivo que debe el técnico no cambia.'
                : <>El efectivo que debe el técnico <strong>{delta > 0 ? 'sube' : 'baja'} {fmt(Math.abs(delta))}</strong>
                    {' '}({fmt(cargaAntes)} → {fmt(cargaDespues)}).</>}
            </Alert>
          )}

          {historial && historial.correcciones.length > 0 && (
            <Box>
              <Divider sx={{ mb: 1.5 }} />
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Correcciones anteriores ({historial.correcciones.length})
              </Typography>
              <Stack spacing={1}>
                {historial.correcciones.map((c, i) => (
                  <Box key={i} sx={{ p: 1, borderRadius: 1, bgcolor: 'grey.50' }}>
                    <Typography variant="caption" color="text.secondary">
                      {new Date(c.fecha).toLocaleString('es-DO', {
                        day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
                      })} · {c.por}
                    </Typography>
                    {c.antes && c.despues && (
                      <Typography variant="body2">
                        {fmt(c.antes.amount)} {c.antes.etiqueta} → {fmt(c.despues.amount)} {c.despues.etiqueta}
                      </Typography>
                    )}
                    {c.motivo && (
                      <Typography variant="caption" color="text.secondary">{c.motivo}</Typography>
                    )}
                  </Box>
                ))}
              </Stack>
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCerrar} disabled={guardando}>Cancelar</Button>
        <Button onClick={guardar} variant="contained" disabled={guardando || !huboCambio}>
          {guardando ? <CircularProgress size={20} /> : 'Guardar corrección'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CorregirCobroDialog;
